/**
 * Shared email helpers for the app's email fields — single-address fields and
 * comma-delimited recipient lists alike. Every drawer/modal validates through
 * these so the rule is consistent everywhere.
 *
 * Python mirror: backend/app/utils/email_validation.py — keep the two in sync
 * (same regex, same comma/semicolon behavior) when either changes.
 *
 * Separators: comma-only by default. Fields that historically accepted semicolons
 * (EDI monitoring, report/notification recipient lists, contact emails) pass
 * `{ semicolon: true }` so existing `;`-delimited data keeps validating.
 */

/** A single email address — loose but practical; rejects whitespace, commas, and extra `@`. */
export const EMAIL_RE = /^[^\s,@]+@[^\s,@]+\.[^\s,@]+$/;

interface ListOptions {
  /** When true, semicolons also separate addresses (in addition to commas). */
  semicolon?: boolean;
}

/** Split a raw field value into trimmed, non-empty tokens. */
function tokens(v: string, semicolon: boolean): string[] {
  const sep = semicolon ? /[,;]/ : /,/;
  return v.split(sep).map((t) => t.trim()).filter(Boolean);
}

/** True when `v` is a single valid email address (surrounding whitespace ignored). */
export function isValidEmail(v: string): boolean {
  return EMAIL_RE.test(v.trim());
}

/**
 * True when `v` is blank OR every token is a valid address. Blank is treated as
 * valid here — required-ness is enforced separately by each field.
 */
export function isValidEmailList(v: string, { semicolon = false }: ListOptions = {}): boolean {
  if (!v.trim()) return true;
  const parts = tokens(v, semicolon);
  return parts.length > 0 && parts.every((p) => EMAIL_RE.test(p));
}

/** The invalid tokens in `v` (trimmed), for naming them in an error message. */
export function findInvalidEmails(v: string, { semicolon = false }: ListOptions = {}): string[] {
  return tokens(v, semicolon).filter((t) => !EMAIL_RE.test(t));
}
